/* eslint-disable new-cap */
import * as Sentry from '@sentry/react';
import axios, {CancelToken} from 'axios';
import {Data, Effect} from 'effect';
import {InterruptedException, UnknownException} from 'effect/Cause';

import {operationsService} from 'api/operations';
import {SortOrder} from 'shared/constants/common';
import {SortField} from 'shared/models/task/const';
import {OperationModelRawDTO} from 'shared/models/task/task';

import {ApiResponse} from './BatchLoader';

export class BadGatewayError extends Data.TaggedError('BadGatewayError')<{
  message: string;
  cause?: Error;
}> {}

export class HttpError extends Data.TaggedError('HttpError')<{
  statusCode: number;
  message: string;
  cause?: Error;
}> {}

export class GatewayTimeoutError extends Data.TaggedError('GatewayTimeoutError')<{
  message: string;
  cause?: Error;
}> {}

export class NetworkError extends Data.TaggedError('NetworkError')<{
  message: string;
  cause?: Error;
}> {}

export type EffectError =
  | Error
  | InterruptedException
  | HttpError
  | BadGatewayError
  | UnknownException
  | GatewayTimeoutError
  | NetworkError;

export const handleRequestWithCancellation = (error: unknown): EffectError => {
  try {
    if (axios.isCancel(error)) {
      return new InterruptedException('Request cancelled');
    }
    return handleAxiosError(error);
  } catch (e) {
    const errorInfo = {
      type: 'UnexpectedError',
      timestamp: new Date().toISOString(),
      originalError: error,
      stack: e instanceof Error ? e.stack : undefined,
      message: e instanceof Error ? e.message : String(e),
    };

    Sentry.captureException(e, {
      extra: errorInfo,
      tags: {
        errorType: 'RequestHandlingError',
        handlerName: 'handleRequestWithCancellation',
      },
    });

    return new Error('Unexpected error during request handling', e);
  }
};

export const createOperationIdsFetcherEffect = (projectId: string) => {
  return (
    offset: number,
    take: number,
    cancelToken: CancelToken,
  ): Effect.Effect<ApiResponse<OperationModelRawDTO>, EffectError, never> =>
    Effect.tryPromise({
      try: () =>
        operationsService.getOperations(projectId, {
          offset,
          limit: take,
          sortOrder: SortOrder.DESC,
          sortField: SortField.outlineSortKey,
          cancelToken,
        }),
      catch: handleRequestWithCancellation,
    }).pipe(
      Effect.map((response) => ({
        data: response.data as OperationModelRawDTO[],
        headers: response.headers,
      })),
    );
};

export const handleInteruptableErrors = (error: unknown): EffectError => {
  if (axios.isCancel(error)) {
    return new InterruptedException('Request cancelled');
  }

  if (axios.isAxiosError(error)) {
    switch (error.response?.status) {
      case 502:
        return new BadGatewayError({
          message: error.message,
          cause: error,
        });
      default:
        return new HttpError({
          statusCode: error.response?.status || 500,
          message: error.message,
          cause: error,
        });
    }
  }

  return new UnknownException({
    message: error instanceof Error ? error.message : 'Unknown error',
    cause: error instanceof Error ? error : undefined,
  });
};

export const handleAxiosError = (error: unknown): EffectError => {
  if (axios.isAxiosError(error)) {
    // Check for network errors first (no response)
    if (!error.response) {
      return new NetworkError({
        message: error.message || 'Network Error',
        cause: error,
      });
    }

    switch (error.response.status) {
      case 502:
        return new BadGatewayError({
          message: error.message,
          cause: error,
        });
      default:
        return new HttpError({
          statusCode: error.response.status || 500,
          message: error.message,
          cause: error,
        });
    }
  }

  return new UnknownException({
    message: error instanceof Error ? error.message : 'An unknown error occurred',
    cause: error instanceof Error ? error : undefined,
  });
};
